import { useState } from "react";
import { useParams, Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import BackToTop from "@/components/feature/BackToTop";
import MangaCard from "@/components/feature/MangaCard";
import { mangaList } from "@/mocks/manga";

export default function DetailPage() {
  const { id } = useParams<{ id: string }>();
  const manga = mangaList.find((m) => m.id === id);
  const [readAll, setReadAll] = useState(false);
  const [fav, setFav] = useState(false);

  if (!manga) {
    return (
      <>
        <Navbar />
        <main className="flex min-h-[60vh] flex-col items-center justify-center pt-16 text-center md:pt-[72px]">
          <i className="ri-emotion-sad-line text-5xl text-primary-300"></i>
          <h1 className="mt-4 font-display text-2xl text-foreground-950">没有找到这部作品</h1>
          <Link
            to="/"
            className="mt-6 flex items-center gap-2 rounded-full bg-primary-500 px-6 py-2.5 text-sm font-medium text-background-50"
          >
            返回首页
            <i className="ri-arrow-right-line"></i>
          </Link>
        </main>
        <Footer />
      </>
    );
  }

  const related = mangaList.filter((m) => m.id !== manga.id).slice(0, 5);
  const shownChapters = readAll ? manga.chapters : manga.chapters.slice(0, 3);

  const schema = {
    "@context": "https://schema.org",
    "@type": "Book",
    name: manga.title,
    author: { "@type": "Person", name: manga.author },
    genre: manga.categoryName,
    description: manga.description,
    image: manga.cover,
    url: `https://emovibe1.github.io/detail/${manga.id}`,
    inLanguage: "zh-CN",
    numberOfPages: manga.chapterCount,
    aggregateRating: {
      "@type": "AggregateRating",
      ratingValue: manga.rating,
      reviewCount: 1286,
    },
  };

  const breadcrumb = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "首页", item: "https://emovibe1.github.io/" },
      {
        "@type": "ListItem",
        position: 2,
        name: `${manga.categoryName}漫画`,
        item: `https://emovibe1.github.io/category/${manga.categoryId}`,
      },
      { "@type": "ListItem", position: 3, name: manga.title },
    ],
  };

  return (
    <>
      <Navbar />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumb) }} />
      <main className="pt-16 md:pt-[72px]">
        <div className="mx-auto w-full max-w-[1400px] px-4">
          <nav aria-label="面包屑" className="flex flex-wrap items-center gap-1.5 py-5 text-sm text-foreground-500">
            <Link to="/" className="transition-colors hover:text-primary-600">首页</Link>
            <i className="ri-arrow-right-s-line"></i>
            <Link to={`/category/${manga.categoryId}`} className="transition-colors hover:text-primary-600">
              {manga.categoryName}漫画
            </Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-foreground-800">{manga.title}</span>
          </nav>

          <div className="grid grid-cols-1 gap-8 lg:grid-cols-3">
            {/* 左：封面与操作 */}
            <div className="lg:col-span-1">
              <div className="lg:sticky lg:top-24">
                <div className="overflow-hidden rounded-2xl border border-background-200/70 bg-background-50">
                  <div className="relative aspect-[3/4] w-full overflow-hidden">
                    <img
                      src={manga.cover}
                      alt={`${manga.title} ${manga.categoryName}恋爱漫画在线看`}
                      className="h-full w-full object-cover object-top"
                    />
                    <span
                      className={`absolute left-3 top-3 rounded-full px-3 py-1 text-xs font-medium text-background-50 ${
                        manga.status === "已完结" ? "bg-accent-500" : "bg-primary-500"
                      }`}
                    >
                      {manga.status}
                    </span>
                  </div>
                  <div className="p-4">
                    <div className="flex items-center justify-between text-sm text-foreground-600">
                      <span className="flex items-center gap-1.5">
                        <i className="ri-star-fill text-accent-500"></i>
                        <b className="text-foreground-950">{manga.rating}</b> 分
                      </span>
                      <span className="flex items-center gap-1.5">
                        <i className="ri-eye-line"></i>
                        {manga.viewers} 人气
                      </span>
                    </div>
                    <div className="mt-3 flex gap-2">
                      <Link
                        to="/#new"
                        className="flex flex-1 items-center justify-center gap-2 whitespace-nowrap rounded-full bg-primary-500 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
                      >
                        <i className="ri-play-circle-line"></i>
                        开始阅读
                      </Link>
                      <button
                        type="button"
                        onClick={() => setFav((v) => !v)}
                        className={`flex w-12 items-center justify-center rounded-full border py-3 text-sm transition-colors ${
                          fav
                            ? "border-primary-400 bg-primary-100 text-primary-600"
                            : "border-background-300 text-foreground-600 hover:border-primary-300 hover:text-primary-600"
                        }`}
                        aria-label="收藏作品"
                      >
                        <i className={fav ? "ri-heart-fill text-lg" : "ri-heart-line text-lg"}></i>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* 右：详情与章节 */}
            <div className="lg:col-span-2">
              <div className="flex flex-wrap items-center gap-2">
                <span className="rounded-full bg-secondary-100 px-3 py-1 text-xs font-medium text-secondary-900">
                  {manga.categoryName}
                </span>
                {manga.tags.map((t) => (
                  <span key={t} className="rounded-full bg-background-100 px-3 py-1 text-xs text-foreground-600">
                    #{t}
                  </span>
                ))}
              </div>

              <h1 className="mt-3 font-display text-3xl text-foreground-950 md:text-4xl">{manga.title}</h1>
              <p className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-foreground-500">
                <span className="flex items-center gap-1.5">
                  <i className="ri-user-heart-line text-primary-500"></i>
                  作者：{manga.author}
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-archive-2-line"></i>
                  共 {manga.chapterCount} 话
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-time-line"></i>
                  更新于 {manga.updateTime}
                </span>
              </p>

              <div className="mt-4 flex flex-wrap gap-4">
                {manga.themes.map((t, i) => (
                  <div key={t} className="min-w-[120px] rounded-2xl bg-background-100 p-3">
                    <p className="text-xs text-foreground-500">{["核心卖点", "心动看点", "剧情标签"][i] || "剧情标签"}</p>
                    <p className="mt-0.5 text-sm font-bold text-foreground-950">{t}</p>
                  </div>
                ))}
                <div className="min-w-[120px] rounded-2xl bg-secondary-100 p-3">
                  <p className="text-xs text-secondary-800">心动指数</p>
                  <div className="mt-1 flex items-center gap-1.5">
                    <span className="h-1.5 w-16 overflow-hidden rounded-full bg-secondary-200">
                      <span className="block h-full rounded-full bg-secondary-500" style={{ width: `${manga.intensity}%` }}></span>
                    </span>
                    <span className="text-sm font-bold text-secondary-700">{manga.intensity}</span>
                  </div>
                </div>
              </div>

              <div className="mt-5 rounded-2xl border border-background-200/70 bg-background-50 p-5">
                <h2 className="flex items-center gap-2 text-base font-bold text-foreground-950">
                  <i className="ri-book-open-line text-primary-500"></i>
                  作品简介
                </h2>
                <p className="mt-3 text-[15px] leading-relaxed text-foreground-600">{manga.description}</p>
                <p className="mt-3 border-l-2 border-primary-300 bg-primary-50 px-4 py-3 text-sm italic text-primary-700">
                  「{manga.heroLine}」
                </p>
              </div>

              {/* 章节列表 */}
              <div className="mt-6 rounded-2xl border border-background-200/70 bg-background-50 p-5">
                <div className="flex items-center justify-between">
                  <h2 className="flex items-center gap-2 text-base font-bold text-foreground-950">
                    <i className="ri-menu-2-line text-primary-500"></i>
                    章节列表
                  </h2>
                  <span className="text-sm text-foreground-500">最新 · {manga.latestChapter}</span>
                </div>
                <div className="mt-4 grid grid-cols-1 gap-2 sm:grid-cols-2">
                  {shownChapters.map((ch) => (
                    <Link
                      key={ch.id}
                      to="/#new"
                      className="group flex items-center justify-between gap-3 rounded-xl border border-background-200/70 bg-background-50 px-4 py-3 transition-colors hover:border-primary-300 hover:bg-primary-50"
                    >
                      <span className="flex min-w-0 items-center gap-2">
                        <i className="ri-book-2-line shrink-0 text-primary-500"></i>
                        <span className="truncate text-sm font-medium text-foreground-800 group-hover:text-primary-700">
                          {ch.title}
                        </span>
                      </span>
                      <span className="flex shrink-0 items-center gap-3 text-xs text-foreground-500">
                        <span>{ch.views}</span>
                        <span className="hidden sm:inline">{ch.updateTime}</span>
                      </span>
                    </Link>
                  ))}
                </div>
                {manga.chapters.length > 3 && (
                  <button
                    type="button"
                    onClick={() => setReadAll((v) => !v)}
                    className="mt-4 flex w-full items-center justify-center gap-2 rounded-xl border border-dashed border-primary-300 py-2.5 text-sm font-medium text-primary-600 transition-colors hover:bg-primary-50"
                  >
                    {readAll ? "收起章节" : `查看全部 ${manga.chapters.length} 话`}
                    <i className={readAll ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"}></i>
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* 相关推荐 */}
          <section className="border-t border-background-200/70 py-10 md:py-14">
            <div className="flex items-center gap-2">
              <span className="flex h-6 w-6 items-center justify-center rounded-full bg-accent-500 text-background-50">
                <i className="ri-heart-3-fill text-xs"></i>
              </span>
              <h2 className="text-lg font-bold text-foreground-950">你可能也喜欢</h2>
            </div>
            <div className="mt-6 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
              {related.map((m) => (
                <MangaCard key={m.id} manga={m} />
              ))}
            </div>
          </section>
        </div>
      </main>
      <Footer />
      <BackToTop />
    </>
  );
}